/**
 * 「高级玩法」折叠区(0924 老板拍板④:换我出镜降级)。
 *
 * 默认页面只展示「文案出片 + 样片墙」—— 那是能稳定出片的主路;换我出镜(上传参考视频
 * 换人)受上游审核/时长档/转发件三道坎,0914 起测试机 4 单 4 败,不再与主路同级平铺。
 * 折叠区展开后才出现两种玩法的切换(文案出片 / 换我出镜);收起即回到文案出片
 * (已传的参考视频保留,再展开还在)。
 */
import { ChevronDown, Clapperboard, Wand2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";

export type RealPersonMode = "text" | "swap";

export function RealPersonAdvancedPlay({
  open,
  onOpenChange,
  mode,
  onModeChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RealPersonMode;
  onModeChange: (m: RealPersonMode) => void;
}) {
  return (
    <div
      data-testid="real-advanced"
      className="rounded-[calc(var(--radius)*1.15)] border border-dashed border-border/70"
    >
      <button
        type="button"
        data-testid="real-advanced-toggle"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm"
      >
        <span className="font-medium">高级玩法</span>
        <span className="min-w-0 flex-1 truncate text-2xs text-muted-foreground">
          换我出镜:上传一段自己拍的参考视频,把出镜人换成 ta
        </span>
        <ChevronDown
          className={cn("h-4 w-4 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-3" role="radiogroup" aria-label="玩法">
          <div className="grid grid-cols-2 gap-3">
            <ModeCard
              active={mode === "text"}
              icon={Wand2}
              title="文案出片"
              desc="写一句想拍的画面,AI 直接生成"
              onClick={() => onModeChange("text")}
            />
            <ModeCard
              active={mode === "swap"}
              icon={Clapperboard}
              title="换我出镜"
              desc="传一段参考视频,出镜人换成 ta"
              onClick={() => onModeChange("swap")}
            />
          </div>
          <p className="text-2xs text-muted-foreground">
            换我出镜对参考视频要求高(本人手机原件、时长按档),拿不准就用上面的文案出片或样片。
          </p>
        </div>
      )}
    </div>
  );
}

function ModeCard({
  active,
  icon: Icon,
  title,
  desc,
  onClick,
}: {
  active: boolean;
  icon: typeof Wand2;
  title: string;
  desc: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 border p-3 text-left transition-colors",
        CARD_WALL_RADIUS,
        active ? "border-primary bg-primary/5" : "border-border/70 hover:border-primary/40"
      )}
    >
      <span
        className={cn(
          "grid h-9 w-9 shrink-0 place-items-center rounded-[calc(var(--radius)*0.8)]",
          active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block truncate text-2xs text-muted-foreground">{desc}</span>
      </span>
    </button>
  );
}
